import { useEffect, useState } from "react";

import { Link } from "react-router-dom";

import Header from "../components/Header/Header";
import PricingModal from "../components/PricingModal/PricingModal";
import { useLanguage } from "../contexts/LanguageContext";
import { useUser } from "../contexts/UserContext";
import apiService from "../services/apiService";

import "./ProfilePage.css";

const ProfilePage = () => {
  const { user } = useUser();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [pricingOpen, setPricingOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    apiService
      .getPremiumStatus(user.id)
      .then((response) => {
        if (!response.success || typeof response.user?.isPremium !== "boolean")
          throw new Error("Invalid premium status");
        if (active) setStatus(response.user);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user.id, revision]);

  const unknown = t("profile.unavailable");
  const date = (value) => {
    if (!value) return unknown;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? unknown
      : new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(
          parsed,
        );
  };
  const expired =
    status?.premiumExpired === true ||
    (status?.premiumEndDate &&
      new Date(status.premiumEndDate).getTime() <= Date.now());
  const premium = status?.isPremium === true && !expired;
  const plan = premium
    ? "Premium"
    : status?.isPremium === false || expired
      ? "Free"
      : unknown;
  const subscriptionType = status?.subscriptionType;
  const cycle = ["daily", "monthly", "annual"].includes(subscriptionType)
    ? t(`profile.${subscriptionType}`)
    : unknown;
  const count =
    Number.isInteger(status?.eventCount) && status.eventCount >= 0
      ? status.eventCount
      : null;
  const limit =
    Number.isInteger(status?.eventLimit) && status.eventLimit >= 0
      ? status.eventLimit
      : null;
  const limitLabel =
    limit !== null
      ? limit
      : status?.eventLimit === null && premium
        ? t("profile.unlimited")
        : unknown;
  const name = user.name || user.username || t("profile.account");
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <>
      <Header />
      <main className="profile-page">
        <Link className="profile-back" to="/events/dashboard">
          ← {t("header.myEvents")}
        </Link>
        <div className="profile-heading">
          <div>
            <p className="profile-eyebrow">{t("profile.account")}</p>
            <h1>{t("profile.title")}</h1>
            <p>{t("profile.subtitle")}</p>
          </div>
          <button
            className="btn btn-secondary"
            disabled={loading}
            onClick={() => setRevision((value) => value + 1)}
          >
            {t(loading ? "profile.refreshing" : "profile.refresh")}
          </button>
        </div>
        <div className="profile-grid">
          <section
            className="profile-panel"
            aria-labelledby="profile-personal-title"
          >
            <div className="profile-identity">
              <span className="profile-avatar" aria-hidden="true">
                {initials}
              </span>
              <div>
                <h2 id="profile-personal-title">{name}</h2>
                <p>{t("profile.personal")}</p>
              </div>
            </div>
            <dl className="profile-details">
              <div>
                <dt>{t("profile.name")}</dt>
                <dd>{user.name || user.username || unknown}</dd>
              </div>
              <div>
                <dt>{t("profile.email")}</dt>
                <dd>{user.email || unknown}</dd>
              </div>
              <div>
                <dt>{t("profile.username")}</dt>
                <dd>{user.username || unknown}</dd>
              </div>
            </dl>
            <p className="profile-note">
              {t("profile.accountHelp")}{" "}
              <Link to="/contact">{t("header.contact")}</Link>
            </p>
          </section>
          <section
            className="profile-panel profile-plan"
            aria-labelledby="profile-plan-title"
            aria-busy={loading}
          >
            <p className="profile-eyebrow">{t("profile.subscription")}</p>
            <h2 id="profile-plan-title">{t("profile.currentPlan")}</h2>
            {loading ? (
              <div className="profile-skeleton" role="status">
                <span className="profile-sr-only">
                  {t("profile.refreshing")}
                </span>
                <i />
                <i />
                <i />
              </div>
            ) : error ? (
              <div className="profile-error" role="alert">
                <p>{t("profile.error")}</p>
                <button
                  className="btn btn-secondary"
                  onClick={() => setRevision((value) => value + 1)}
                >
                  {t("profile.retry")}
                </button>
              </div>
            ) : (
              <>
                <div className="profile-plan-heading">
                  <strong>{plan}</strong>
                  <span
                    className={`profile-badge ${premium ? "is-premium" : ""}`}
                  >
                    {t(
                      premium
                        ? "profile.active"
                        : expired
                          ? "profile.expired"
                          : "profile.noSubscription",
                    )}
                  </span>
                </div>
                <dl className="profile-details">
                  <div>
                    <dt>{t("profile.paymentType")}</dt>
                    <dd>{t("profile.paymentInfo")}</dd>
                  </div>
                  {premium && (
                    <div>
                      <dt>{t("profile.cycle")}</dt>
                      <dd>{cycle}</dd>
                    </div>
                  )}
                  {(premium || expired) && (
                    <>
                      <div>
                        <dt>{t("profile.startDate")}</dt>
                        <dd>{date(status.premiumStartDate)}</dd>
                      </div>
                      <div>
                        <dt>{t("profile.endDate")}</dt>
                        <dd>{date(status.premiumEndDate)}</dd>
                      </div>
                    </>
                  )}
                  <div>
                    <dt>{t("profile.eventLimit")}</dt>
                    <dd>{limitLabel}</dd>
                  </div>
                </dl>
                <div className="profile-usage">
                  <div>
                    <span>{t("profile.eventsUsed")}</span>
                    <strong>
                      {count ?? unknown}
                      {count !== null && limit !== null ? ` / ${limit}` : ""}
                    </strong>
                  </div>
                  {count !== null && limit > 0 && (
                    <progress
                      value={Math.min(count, limit)}
                      max={limit}
                      aria-label={t("profile.eventsUsed")}
                    />
                  )}
                  {count === 0 && <p>{t("profile.emptyEvents")}</p>}
                </div>
                <p className="profile-note">
                  {t(
                    premium
                      ? "profile.billingNote"
                      : expired
                        ? "profile.expiredNote"
                        : "profile.freeNote",
                  )}
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => setPricingOpen(true)}
                >
                  {t(premium ? "profile.viewPlans" : "header.upgrade")}
                </button>
              </>
            )}
          </section>
        </div>
        <aside className="profile-support">
          <div>
            <h2>{t("profile.supportTitle")}</h2>
            <p>{t("profile.supportText")}</p>
          </div>
          <Link className="btn btn-outline" to="/contact">
            {t("header.contact")}
          </Link>
        </aside>
      </main>
      {pricingOpen && (
        <PricingModal
          isOpen
          onClose={() => {
            setPricingOpen(false);
            setRevision((value) => value + 1);
          }}
        />
      )}
    </>
  );
};

export default ProfilePage;
